import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { RotatingArrows } from '@creditornot/cb-icons';
import { typographyCss } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ConfirmModal } from 'common/components';

import { StyledTextarea } from './OfferDetailsFormFields.styles';
import { DESCRIPTION_MAX_LENGTH } from './constants';
import { DescriptionType } from '../../hooks/useAiDescriptions';

type GenerateDescriptionModalProps = {
  onCancel: () => void;
  onConfirm: (chosenDescription: string) => void;
  onGenerate: ({ info, type }: { info: string; type: DescriptionType }) => void;
  isLoading: boolean;
  generatedDescription: string;
  trackViewModal: () => void;
};

const StyledConfirmModal = styled(ConfirmModal)`
  width: 100%;
  max-width: 480px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${typographyCss.Body3()}
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ClickableTag = styled(Tag).attrs({ role: 'button' })`
  cursor: pointer;
`;

export const GenerateDescriptionModal = ({
  onCancel,
  onConfirm,
  onGenerate,
  isLoading,
  generatedDescription,
  trackViewModal,
}: GenerateDescriptionModalProps) => {
  const [descriptionType, setDescriptionType] = useState<DescriptionType>('original');
  const intl = useIntl();

  trackViewModal();

  const tags = useMemo<{ type: DescriptionType; label: string; onClick: () => void }[]>(
    () => [
      {
        type: 'original',
        label: intl.formatMessage({ id: 'menu-item.ai-description.original-button' }),
        onClick: () => setDescriptionType('original'),
      },
      {
        type: 'enhance',
        label: intl.formatMessage({ id: 'menu-item.ai-description.enhance-button' }),
        onClick: () => setDescriptionType('enhance'),
      },
      {
        type: 'formal',
        label: intl.formatMessage({ id: 'menu-item.ai-description.formal-button' }),
        onClick: () => setDescriptionType('formal'),
      },
      {
        type: 'playful',
        label: intl.formatMessage({ id: 'menu-item.ai-description.playful-button' }),
        onClick: () => setDescriptionType('playful'),
      },
    ],
    [intl],
  );

  return (
    <StyledConfirmModal
      cancelText={<FormattedMessage id="state.close" />}
      confirmText={<FormattedMessage id="menu-item.ai-description.modal-accept-button" />}
      description={
        <Content>
          <span>{intl.formatMessage({ id: 'menu-item.ai-description.modal-description' })}</span>
          <TagRow>
            {tags.map(tag => (
              <ClickableTag
                key={tag.type}
                variant={descriptionType === tag.type ? 'darkBlue' : 'lightDarkBlue'}
                onClick={tag.onClick}
              >
                {tag.label}
              </ClickableTag>
            ))}
          </TagRow>
          <StyledTextarea
            disabled={isLoading}
            value={generatedDescription}
            maxLength={DESCRIPTION_MAX_LENGTH}
            spellCheck
          />
          <div>
            <Button
              size="tiny"
              variant="secondary"
              icon={<RotatingArrows />}
              loading={isLoading}
              onClick={() => onGenerate({ info: generatedDescription, type: descriptionType })}
            >
              {intl.formatMessage({ id: 'menu-item.ai-description.generate-again-button' })}
            </Button>
          </div>
          <div>
            <Alert variant="warning">{intl.formatMessage({ id: 'menu-item.ai-description.alert-text' })}</Alert>
          </div>
        </Content>
      }
      header={intl.formatMessage({ id: 'menu-item.ai-description.modal-title' })}
      loading={isLoading}
      onCancel={onCancel}
      onConfirm={() => onConfirm(generatedDescription)}
      disabled={isLoading}
      open
    />
  );
};
